import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MapPin, Clock, BadgePoundSterling, Building2, Bookmark, ArrowLeft, Globe, BadgeCheck, Upload, Star } from "lucide-react";
import { toast } from "sonner";
import { api, formatSalary, timeAgo, SITE_URL, formatApiError, uploadFile, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";
import ShareButtons from "../components/ShareButtons";
import { JobAlertControl } from "../components/JobAlertControl";
import JobCourses from "../components/JobCourses";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const EMPLOYMENT_TYPE_MAP = {
  "Full-time": "FULL_TIME", "Part-time": "PART_TIME", "Contract": "CONTRACTOR",
  "Temporary": "TEMPORARY", "Apprenticeship": "INTERN",
};
const UNIT_MAP = { year: "YEAR", month: "MONTH", week: "WEEK", day: "DAY", hour: "HOUR" };

function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={`b-${i}-${p.length}`} className="font-semibold text-zinc-900">{p.slice(2, -2)}</strong>
      : p
  );
}

function renderDescription(text) {
  const blocks = [];
  let list = [];
  const flush = (key) => {
    if (list.length) {
      blocks.push(
        <ul key={key} className="list-disc pl-5 space-y-1.5 my-3">
          {list.map((item, i) => <li key={`${i}-${item.slice(0, 24)}`}>{renderInline(item)}</li>)}
        </ul>
      );
      list = [];
    }
  };
  text.split("\n").forEach((line, idx) => {
    const t = line.trim();
    if (t.startsWith("- ")) {
      list.push(t.slice(2));
    } else {
      flush(`l${idx}`);
      if (t.startsWith("### ")) {
        blocks.push(<h3 key={`block-${idx}`} className="font-display text-lg font-bold text-zinc-950 mt-6 mb-2">{renderInline(t.slice(4))}</h3>);
      } else if (t) {
        blocks.push(<p key={`block-${idx}`} className="my-3">{renderInline(t)}</p>);
      }
    }
  });
  flush("end");
  return blocks;
}

function buildJobPostingSchema(job) {
  const schema = {
    "@type": "JobPosting",
    "title": job.title,
    "description": `<p>${job.description.replace(/\n/g, "</p><p>")}</p>`,
    "datePosted": (job.live_at || job.created_at || "").split("T")[0],
    "employmentType": EMPLOYMENT_TYPE_MAP[job.job_type] || "FULL_TIME",
    "directApply": true,
    "identifier": {
      "@type": "PropertyValue",
      "name": job.company_name,
      "value": job.slug,
    },
    "hiringOrganization": {
      "@type": "Organization",
      "name": job.company_name,
      ...(job.company_website ? { "sameAs": job.company_website } : {}),
    },
    "jobLocation": [job.location, ...(job.boost_locations || [])].map((loc) => ({
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": loc,
        "addressCountry": "GB",
      },
    })),
    "url": `${SITE_URL}/jobs/${job.slug}`,
  };
  if (job.expires_at) schema.validThrough = job.expires_at.split("T")[0];
  if (job.workplace === "Remote") {
    schema.jobLocationType = "TELECOMMUTE";
    schema.applicantLocationRequirements = { "@type": "Country", "name": "United Kingdom" };
  }
  if (job.show_salary && (job.salary_min || job.salary_max)) {
    schema.baseSalary = {
      "@type": "MonetaryAmount",
      "currency": "GBP",
      "value": {
        "@type": "QuantitativeValue",
        ...(job.salary_min ? { "minValue": job.salary_min } : {}),
        ...(job.salary_max ? { "maxValue": job.salary_max } : {}),
        "unitText": UNIT_MAP[job.salary_period] || "YEAR",
      },
    };
  }
  return schema;
}

export default function JobDetail({ embedded = false, embeddedSlug = "", onClear = null }) {
  const params = useParams();
  const slug = embeddedSlug || params.slug;
  const { user } = useAuth();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [applied, setApplied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState({ phone: "", cover_note: "", cv_link: "", cv_path: "", answers: {} });
  const [cvFile, setCvFile] = useState(null);
  const [cvUploading, setCvUploading] = useState(false);

  const uploadCv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCvUploading(true);
    try {
      const r = await uploadFile(file, "cv");
      setCvFile({ path: r.path, filename: r.filename });
      setForm((f) => ({ ...f, cv_path: r.path }));
      toast.success(`${file.name} attached`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCvUploading(false);
      e.target.value = "";
    }
  };

  const utmParams = useMemo(() => {
    const utm = new URLSearchParams(window.location.search);
    const p = {};
    ["utm_source", "utm_medium", "utm_campaign"].forEach((k) => { if (utm.get(k)) p[k] = utm.get(k); });
    return p;
  }, [slug]);

  useEffect(() => {
    setJob(null);
    setNotFound(false);
    api.get(`/jobs/${slug}`, { params: utmParams })
      .then((res) => {
        setJob(res.data);
        setForm({ phone: "", cover_note: "", cv_link: "", cv_path: "", answers: {} });
        setCvFile(null);
      })
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
        else toast.error("Could not load this job. Please refresh.");
      });
  }, [slug, utmParams]);

  useEffect(() => {
    if (user && user.role === "candidate") {
      setForm((f) => ({
        ...f,
        phone: f.phone || user.phone || "",
        cv_link: f.cv_link || user.cv_link || "",
        cv_path: f.cv_path || user.cv_path || "",
      }));
      api.get("/candidate/applications").then((res) => {
        if (res.data.some((a) => a.job_slug === slug)) setApplied(true);
      }).catch(() => {});
      api.get("/candidate/saved").then((res) => {
        if (res.data.some((j) => j.slug === slug)) setSaved(true);
      }).catch(() => {});
    }
  }, [user, slug]);

  const toggleSave = async () => {
    if (!user) return navigate(`/login?next=${encodeURIComponent(`/jobs/${slug}`)}`);
    if (user.role !== "candidate") return;
    try {
      if (saved) {
        await api.delete(`/candidate/saved/${job.id}`);
        setSaved(false);
        toast.success("Removed from saved jobs");
      } else {
        await api.post(`/candidate/saved/${job.id}`);
        setSaved(true);
        toast.success("Saved. Find it in your dashboard");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const fastApply = async () => {
    setSending(true);
    api.post(`/jobs/${slug}/apply-start`, {}, { params: utmParams }).catch(() => {});
    try {
      await api.post(`/jobs/${slug}/apply`, {
        phone: user.phone || "", cover_note: "", cv_link: user.cv_link || "", cv_path: user.cv_path || "", answers: {},
      }, { params: utmParams });
      setApplied(true);
      toast.success("Application sent. The employer has your details now.");
    } catch (err) {
      toast.error(formatApiError(err, "Could not send your application"));
    } finally {
      setSending(false);
    }
  };

  const submitApplication = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post(`/jobs/${slug}/apply`, form, { params: utmParams });
      setApplied(true);
      setApplyOpen(false);
      toast.success("Application sent. The employer has your details now.");
    } catch (err) {
      toast.error(formatApiError(err, "Could not send your application"));
    } finally {
      setSending(false);
    }
  };

  const jsonLd = useMemo(() => {
    if (!job) return null;
    return {
      "@context": "https://schema.org",
      "@graph": [
        buildJobPostingSchema(job),
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
            { "@type": "ListItem", "position": 2, "name": "Jobs", "item": `${SITE_URL}/jobs` },
            { "@type": "ListItem", "position": 3, "name": job.title },
          ],
        },
      ],
    };
  }, [job]);

  if (notFound) {
    return (
      <main className={embedded ? "px-2 py-10 text-center" : "max-w-3xl mx-auto px-4 py-24 text-center"} data-testid="job-not-found">
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-zinc-950">This role is no longer available</h1>
        <p className="mt-3 text-zinc-600">It may have been filled or taken down. Plenty more where it came from.</p>
        {embedded ? (
          <button onClick={onClear} data-testid="job-not-found-clear"
            className="mt-6 inline-block bg-zinc-950 text-white text-sm font-semibold px-6 py-3 rounded-md">
            Back to search results
          </button>
        ) : (
          <Link to="/jobs" className="mt-6 inline-block bg-zinc-950 text-white text-sm font-semibold px-6 py-3 rounded-md">
            Browse live jobs
          </Link>
        )}
      </main>
    );
  }

  if (!job) {
    return (
      <main className={embedded ? "px-2 py-6 animate-pulse" : "max-w-5xl mx-auto px-4 py-20 animate-pulse"} data-testid="job-loading">
        <div className="h-8 bg-zinc-100 rounded w-1/2 mb-4" />
        <div className="h-4 bg-zinc-100 rounded w-1/3" />
      </main>
    );
  }

  const salary = formatSalary(job);
  // return to the exact search position/filters the jobseeker came from
  let backTo = "/jobs";
  try { backTo = `/jobs${sessionStorage.getItem("jobs:return") || ""}`; } catch (_) { /* best-effort */ }

  return (
    <main className={embedded ? "" : "max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14"} data-testid="job-detail-page">
      {!embedded && (
      <Seo
        title={`${job.title} at ${job.company_name} · ${job.location} | HiredToday`}
        description={`${job.company_name} is hiring a ${job.title} in ${job.location}. ${job.job_type}, ${job.workplace}. Apply free on HiredToday.`}
        path={`/jobs/${job.slug}`}
        image={`${SITE_URL}/api/og/job/${job.slug}.png`}
        jsonLd={jsonLd}
      />
      )}

      {!embedded && (
      <Link to={backTo} data-testid="back-to-jobs" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 transition-colors duration-200 mb-8">
        <ArrowLeft className="h-4 w-4" /> Back to search results
      </Link>
      )}

      <div className={embedded ? "grid grid-cols-1 gap-6 items-start" : "grid lg:grid-cols-3 gap-10 items-start"}>
        <div className="lg:col-span-2">
          <div className="flex items-start gap-4">
            {job.company_logo ? (
              <img src={fileUrl(job.company_logo)} alt={`${job.company_name} logo`} data-testid="job-company-logo"
                onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
                className="shrink-0 h-14 w-14 rounded-md object-cover border border-zinc-200 bg-white" />
            ) : null}
            <div style={job.company_logo ? { display: "none" } : undefined}
              className={`shrink-0 h-14 w-14 rounded-md flex items-center justify-center font-display font-bold text-xl ${
              job.featured ? "bg-yellow-100 text-yellow-800" : "bg-zinc-100 text-zinc-700"}`}>
              {job.company_name?.charAt(0)?.toUpperCase()}
            </div>
            <div>
              {job.featured && (
                <span className="inline-block text-[11px] font-bold uppercase tracking-wider bg-yellow-400 text-zinc-950 px-2 py-0.5 rounded mb-2">Featured</span>
              )}
              {job.urgent && (
                <span className="inline-block text-[11px] font-bold uppercase tracking-wider bg-red-100 text-red-700 px-2 py-0.5 rounded mb-2 ml-2" data-testid="urgent-badge">Urgent</span>
              )}
              <h1 className={`font-display tracking-tight font-bold text-zinc-950 leading-tight ${embedded ? "text-xl sm:text-2xl" : "text-3xl sm:text-4xl"}`} data-testid="job-title">
                {job.title}
              </h1>
              <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                {job.company_slug ? (
                  <Link to={`/companies/${job.company_slug}`} data-testid="job-company"
                    className="inline-block text-zinc-600 font-medium hover:text-blue-700 transition-colors duration-200">
                    {job.company_name}
                  </Link>
                ) : (
                  <p className="text-zinc-600 font-medium" data-testid="job-company">{job.company_name}</p>
                )}
                <span title={job.employer_verified ? "Verified company" : "Not verified yet"} data-testid="verified-badge"
                  className="inline-flex shrink-0">
                  <BadgeCheck className={`h-4 w-4 ${job.employer_verified ? "text-blue-600" : "text-zinc-300"}`} />
                </span>
                {job.ref && <span className="text-xs text-zinc-400 ml-auto" data-testid="job-ref">Ref {job.ref}</span>}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2.5 mt-6" data-testid="job-meta">
            <span className="inline-flex items-center gap-1.5 text-sm bg-zinc-100 text-zinc-700 px-3 py-1.5 rounded-full"><MapPin className="h-4 w-4" />{job.location}</span>
            <span className="inline-flex items-center gap-1.5 text-sm bg-zinc-100 text-zinc-700 px-3 py-1.5 rounded-full"><Clock className="h-4 w-4" />{job.job_type}</span>
            <span className="inline-flex items-center gap-1.5 text-sm bg-zinc-100 text-zinc-700 px-3 py-1.5 rounded-full"><Building2 className="h-4 w-4" />{job.workplace}</span>
            {salary && <span className="inline-flex items-center gap-1.5 text-sm bg-zinc-100 text-zinc-700 px-3 py-1.5 rounded-full"><BadgePoundSterling className="h-4 w-4" />{salary}</span>}
          </div>

          <div className="mt-10">
            {job.description.trimStart().startsWith("### ") ? null : (
              <h2 className="font-display text-xl font-bold text-zinc-950 mb-4">The role</h2>
            )}
            <div className="text-base text-zinc-700 leading-relaxed" data-testid="job-description">
              {renderDescription(job.description)}
            </div>
          </div>

          {job.company_description && (
            <div className="mt-10 border-t border-zinc-100 pt-8">
              <h2 className="font-display text-xl font-bold text-zinc-950 mb-4">About {job.company_name}</h2>
              <p className="text-base text-zinc-700 leading-relaxed whitespace-pre-line">{job.company_description}</p>
              {job.company_website && (
                <a href={job.company_website.startsWith("http") ? job.company_website : `https://${job.company_website}`}
                  target="_blank" rel="noopener noreferrer" data-testid="company-website-link"
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 hover:text-blue-900">
                  <Globe className="h-4 w-4" /> Visit company website
                </a>
              )}
            </div>
          )}
        </div>

        {/* embedded (split pane): the apply/share card sits at the END of the scroll,
            after the role description, instead of pinned on top */}
        <aside className={embedded ? "mt-8 border-t border-zinc-200/70 pt-6" : "lg:sticky lg:top-24"}>
          <div className={embedded ? "border border-white/60 rounded-lg p-5 bg-white/75 backdrop-blur-sm shadow-sm" : "border border-zinc-200 rounded-md p-5 bg-white"} data-testid="apply-box">
            <p className="text-xs text-zinc-400 mb-4 text-center">
              Posted {timeAgo(job.live_at || job.created_at)}{job.expires_at ? ` · Closes ${new Date(job.expires_at).toLocaleDateString("en-GB", { day: "numeric", month: "long" })}` : ""}
            </p>
            {applied ? (
              <div className="bg-green-50 border border-green-200 rounded-md p-4 text-sm text-green-800 font-medium" data-testid="applied-confirmation">
                Application sent. The employer will be in touch directly if you're a fit.
              </div>
            ) : !user ? (
              <div className="space-y-3">
                <button onClick={() => navigate(`/login?next=${encodeURIComponent(`/jobs/${slug}`)}`)} data-testid="apply-login-button"
                  className="w-full h-12 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200">
                  Log in to apply
                </button>
                <p className="text-xs text-zinc-500 text-center">
                  New here? <Link to={`/register?next=${encodeURIComponent(`/jobs/${slug}`)}&source=apply_gate`} data-testid="apply-register-link" className="text-blue-700 font-medium">Create a free account</Link>
                </p>
              </div>
            ) : user.role === "candidate" ? (
              <div className="space-y-2.5">
                {(job.screening_questions || []).length === 0 && user.cv_path && (
                  <div className="relative group">
                    <button onClick={fastApply} disabled={sending} data-testid="fast-apply-button"
                      className="w-full h-12 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
                      {sending ? "Sending…" : "Fast apply"}
                    </button>
                    <span data-testid="fast-apply-tooltip"
                      className="pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-zinc-900 px-2.5 py-1.5 text-[11px] font-medium text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-lg z-10">
                      Sends your saved profile & CV instantly
                    </span>
                  </div>
                )}
                <button onClick={() => { setApplyOpen(true); api.post(`/jobs/${slug}/apply-start`, {}, { params: utmParams }).catch(() => {}); }} data-testid="apply-button"
                  className={`w-full h-12 text-sm font-semibold rounded-md transition-colors duration-200 ${
                    (job.screening_questions || []).length === 0 && user.cv_path
                      ? "bg-zinc-50 border border-zinc-200 text-zinc-700 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-800"
                      : "bg-zinc-950 text-white hover:bg-zinc-800"}`}>
                  {(job.screening_questions || []).length > 0 ? "Apply now" : (user.cv_path ? "Apply with a note" : "Apply now")}
                </button>
              </div>
            ) : (
              <p className="text-sm text-zinc-500 text-center">You're signed in with an employer account, so applying isn't available here.</p>
            )}
            {!applied && job.external_apply_url && (
              <div className="mt-3" data-testid="external-apply-alt">
                {user && <p className="text-xs text-zinc-400 text-center mb-2">or</p>}
                <a href={job.external_apply_url} target="_blank" rel="noopener noreferrer"
                  data-testid="external-apply-button"
                  onClick={() => api.post(`/jobs/${slug}/external-click`).catch(() => {})}
                  className="w-full min-h-10 py-2 px-4 bg-zinc-50 border border-zinc-200 text-zinc-700 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-800 text-xs font-medium rounded-md transition-colors duration-200 inline-flex items-center justify-center gap-1.5 text-center">
                  <Globe className="h-3.5 w-3.5 shrink-0" />
                  <span className="leading-snug">Apply on {job.company_name}'s website</span>
                </a>
              </div>
            )}
            <div className="mt-4 pt-4 border-t border-zinc-100 space-y-3">
              {user && user.role === "candidate" && (
                <div className="flex justify-center">
                  <button onClick={toggleSave} data-testid="save-job-button"
                    className={`h-8 px-4 text-xs font-semibold inline-flex items-center gap-1.5 rounded-md border whitespace-nowrap transition-colors duration-200 ${
                      saved
                        ? "border-yellow-200 bg-yellow-50 text-yellow-800"
                        : "border-zinc-200 bg-zinc-50 text-zinc-500 hover:bg-yellow-50 hover:border-yellow-200 hover:text-yellow-800"}`}>
                    <Bookmark className={`h-3.5 w-3.5 ${saved ? "fill-yellow-400 text-yellow-500" : ""}`} />
                    {saved ? "Saved" : "Save for later"}
                  </button>
                </div>
              )}
              <ShareButtons path={`/jobs/${job.slug}`} title={`${job.title} at ${job.company_name}`} testid="job" shareUrl={`${SITE_URL}/api/share/jobs/${job.slug}`} compact />
              <JobCourses slug={job.slug} />
            </div>
          </div>
          <div className="mt-4">
            <JobAlertControl location={(job.location || "").split(",")[0]} category={job.category || ""}
              label="Get new jobs like this by email" />
          </div>
        </aside>
      </div>

      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent className="bg-white sm:max-w-lg" data-testid="apply-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold">Apply for {job.title}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitApplication} className="space-y-4 mt-2">
            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1.5">Phone (optional)</label>
              <input data-testid="apply-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="07..."
                className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1.5">Your CV (optional)</label>
              {cvFile ? (
                <p className="text-xs text-zinc-600 flex items-center justify-between bg-white border border-zinc-200 rounded-md px-3 py-2.5" data-testid="apply-cv-attached">
                  {cvFile.filename}
                  <button type="button" onClick={() => { setCvFile(null); setForm({ ...form, cv_path: user?.cv_path || "" }); }}
                    data-testid="apply-cv-remove" className="text-red-600 font-semibold">Remove</button>
                </p>
              ) : form.cv_path && user?.cv_filename ? (
                <p className="text-xs text-zinc-600 flex items-center justify-between bg-white border border-zinc-200 rounded-md px-3 py-2.5" data-testid="apply-cv-saved">
                  Attaching your saved CV: {user.cv_filename}
                  <label className="text-blue-700 font-semibold cursor-pointer" data-testid="apply-cv-replace">
                    Replace
                    <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={uploadCv} disabled={cvUploading} />
                  </label>
                </p>
              ) : (
                <label className="flex items-center justify-center gap-2 h-11 border border-dashed border-zinc-300 rounded-md text-xs font-semibold text-zinc-600 cursor-pointer hover:border-zinc-500 transition-colors duration-200" data-testid="apply-cv-upload">
                  <Upload className="h-3.5 w-3.5" /> {cvUploading ? "Uploading…" : "Upload your CV (PDF or image)"}
                  <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={uploadCv} disabled={cvUploading} />
                </label>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1.5">Link to your CV or LinkedIn (optional)</label>
              <input data-testid="apply-cv-link" value={form.cv_link} onChange={(e) => setForm({ ...form, cv_link: e.target.value })}
                placeholder="https://"
                className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1.5">A few words for the employer</label>
              <textarea data-testid="apply-cover-note" value={form.cover_note} onChange={(e) => setForm({ ...form, cover_note: e.target.value })}
                rows={5} placeholder="Why this role suits you. Keep it honest and short."
                className="w-full px-3 py-2.5 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 resize-y" />
            </div>
            {(job.screening_questions || []).length > 0 && (
              <div className="space-y-3 border-t border-zinc-100 pt-4" data-testid="apply-screening">
                <p className="text-sm font-medium text-zinc-700">The employer asks:</p>
                {job.screening_questions.map((q, i) => (
                  <div key={`sq-${i}`}>
                    <label className="text-sm text-zinc-600 block mb-1">{q}</label>
                    <input data-testid={`apply-answer-${i}`} value={form.answers[q] || ""} required
                      onChange={(e) => setForm({ ...form, answers: { ...form.answers, [q]: e.target.value } })}
                      className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                  </div>
                ))}
              </div>
            )}
            <button type="submit" disabled={sending} data-testid="apply-submit"
              className="w-full h-12 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
              {sending ? "Sending…" : "Send application"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </main>
  );
}
